/**
 * What: "Running now" on the Tests page: cards for the two newest queued or running tests, side by side, above the
 *   Tests resource index. Each card is the existing `ActiveRunCard` (status, response progress, step checklist).
 * Transport / data-flow: Presentational. Receives the active records built by `buildTestsIndexRecords` (newest
 *   first). Renders nothing when no test is running. Other running tests stay in the
 *   table's Running tab.
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { CardGrid, CardGridItem } from './CardGrid'
import { InfoButton, InfoPopover } from './shared/PageHelp'
import { ActiveRunCard } from './tests-index/parts/ActiveRunCard'
import type { TestsIndexRecord } from './tests-index/types'

/** Owner decision (#4348): at most two running cards. */
export const RUNNING_CARD_LIMIT = 2

const INFO_ID = 'tests-running-now-info'

export function RunningTestsSection({
  records,
  onOpen,
  onRetryStatus,
}: {
  records: readonly TestsIndexRecord[]
  onOpen: (record: TestsIndexRecord) => void
  onRetryStatus: () => void
}) {
  const copy = shopifyComponentCopy.testsIndex
  const shown = records.slice(0, RUNNING_CARD_LIMIT)
  if (shown.length === 0) return null
  return (
    <s-section>
      <s-grid gap="base">
        <s-stack direction="inline" gap="small-200" alignItems="center">
          <s-heading>{copy.activeHeading}</s-heading>
          <InfoButton id={INFO_ID} heading={copy.activeHeading} />
        </s-stack>
        <CardGrid minColumnWidth={320} maxColumns={RUNNING_CARD_LIMIT} gap={16} accessibilityLabel={copy.activeHeading}>
          {shown.map((record) => (
            <CardGridItem key={record.key}>
              <ActiveRunCard record={record} onOpen={() => onOpen(record)} onRetryStatus={onRetryStatus} />
            </CardGridItem>
          ))}
        </CardGrid>
      </s-grid>
      <InfoPopover id={INFO_ID} why={copy.activeInfo} how={copy.activeInfoHow} />
    </s-section>
  )
}
